import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {BaseComponent} from "../base.component";
import {IShareLinkOptions, IShareList, IShareListProduct} from "./social-models";
import {IWarehouseConfig} from "../session/customer-models";
import {Constants} from "../constants";
import {LoggerService} from "../common/logger.service";
import {ShareListService} from "./share-list.service";
import {ConfirmDialogService} from "../common/confirm-dialog/confirm-dialog.service";
import {MarkupService} from "../common/markup.service";
import {AnalyticsService} from "../analytics/analytics.service";
import {IProduct} from "../product/product-models";
import {SessionService} from "../common/session.service";
import {FacebookButtonComponent} from "./facebook/facebook-button.component";
import {Subscription} from "rxjs";
import {UserService} from "../common/user.service";
import {CustomerCacheService} from "../../checkout/customer/customer-cache.service";
import {Customer} from "../../checkout/models/customer/customer";
import {ShareableUtil} from "./shareable-util";
import {GqlShareListService} from "./share-list/gql-share-list.service";
import {ShareListCacheService} from "./share-list/share-list-cache.service";
import {finalize} from "rxjs/operators";

@Component({
    selector: 'share-list',
    templateUrl: './share-list.component.html',
    styleUrls: ['./share-list.component.scss'],
    standalone: false
})
export class ShareListComponent extends BaseComponent implements OnInit, OnDestroy {

    shareLists: IShareList[];
    shareList: IShareList;
    editingName: boolean = false;
    unchangedName: string;
    currentWarehouse: IWarehouseConfig;
    shopMode = Constants.SHOP;
    shareListId: number;
    shareListsSubscription: Subscription;
    shareOptions: IShareLinkOptions;
    sponsorErrorTag: string;
    isBusy: boolean = true;

    @ViewChild(FacebookButtonComponent) private facebookButtonComponent: FacebookButtonComponent;

    constructor(
        loggerService: LoggerService,
        userService: UserService,
        private readonly sessionService: SessionService,
        private readonly gqlShareListService: GqlShareListService,
        private readonly shareListCacheService: ShareListCacheService,
        private readonly shareListService: ShareListService,
        private readonly confirmDialogService: ConfirmDialogService,
        private readonly markupService: MarkupService,
        private readonly analyticsService: AnalyticsService,
        private readonly customerCacheService: CustomerCacheService
    ) {
        super(userService, loggerService);
    }

    ngOnInit() {
        this.shareListsSubscription = this.shareListCacheService.shareListsSubject.subscribe((data: IShareList[]) => {
            if (data?.length === 0) {
                this.shareListService.createNewList([]).subscribe();
            } else if (data?.length > 0) {
                this.isBusy = false;
                this.autoSelectList(data);
            } else { // reset shareLists
                this.shareLists = null;
            }
        });

        this.shareListsSubscription.add(this.customerCacheService.customerSubject.watch().subscribe((customer: Customer) => {
            if (!ShareableUtil.validShareablesSponsor(customer)) {
                // extract tag from response message
                this.sponsorErrorTag = customer.sponsorshipStatus.messages?.length > 0 ? "cart.shareable." + customer.sponsorshipStatus.messages[0] : "cart.shareable.sponsor.validation.error.title";
            }
        }));

        this.sessionService.getCurrentWarehouse().then((currentWarehouse: IWarehouseConfig) => {
            this.currentWarehouse = currentWarehouse;
        });
        this.setPageTitle();
    }

    ngOnDestroy() {
        this.shareListsSubscription.unsubscribe();
    }

    createNewList() {
        if (!this.isBusy) {
            this.isBusy = true;
            this.shareListService.createNewList([])
                .pipe(finalize(() => this.isBusy = false)).subscribe((shareList) => {
                this.setActiveList(shareList);
                this.analyticsService.createShareListEvent('shareList');
            });
        }
    }

    increment(slp: IShareListProduct) {
        this.adjQuantity(slp.usanaId, 1);
    }

    decrement(slp: IShareListProduct) {
        this.adjQuantity(slp.usanaId, -1);
    }

    adjQuantity(usanaId: string, adjQuantity: number) {
        this.gqlShareListService.adjQuantityToShareList(this.shareList.shareListId, usanaId, adjQuantity).subscribe();
    }

    remove(slp: IShareListProduct) {
        this.resetFbModal();
        this.gqlShareListService.removeShareListProduct(slp.shareListProductId).subscribe();
    }

    deleteShareList() {
        const confirm = () => {
            this.isBusy = true;
            this.gqlShareListService.deleteShareList(this.shareList.shareListId)
                .pipe(finalize(() => this.isBusy = false))
                .subscribe(() => {
                    this.analyticsService.deleteShareListEvent('shareList', this.shareList.shareListId);
                });
        }
        if (!this.isBusy) {
            this.confirmDialogService.open("md", {title: "cart.shareable.delete.confirm.title"}, [{
                body: "cart.shareable.confirm.body.text",
                translate: true
            }], {confirmAction: confirm}, {show: true});
        }
    }

    autoSelectList(data: IShareList[]) {
        if (data?.length > 0) {
            const currentListIndex: number = this.shareList && this.shareLists ? this.shareLists.findIndex((sl: IShareList) => sl.shareListId === this.shareList.shareListId) : 0;
            this.shareLists = data;
            if (!this.shareList) {
                // initial selection of first list
                this.setActiveList(this.shareLists[0]);
            } else if (this.shareLists[currentListIndex]?.shareListId === this.shareList?.shareListId) {
                // modifying current list
                this.setActiveList(this.shareLists[currentListIndex]);
            } else if (currentListIndex >= 1 && !!this.shareLists[currentListIndex - 1]) {
                // current list doesn't exist, select list above it
                this.setActiveList(this.shareLists[currentListIndex - 1]);
            } else {
                // select new first list
                this.setActiveList(this.shareLists[0]);
            }
        }
    }

    setActiveList(shareList: IShareList) {
        this.resetFbModal();
        this.cancelEditingName();
        this.shareList = shareList;
        this.shareOptions = {
            shareType: Constants.SHARE_TYPE_SHARE_LIST,
            shareId: shareList.shareListId.toString(),
            shareFrom: Constants.SHARE_LIST_STATE
        };
    }

    saveListName() {
        this.resetFbModal();
        this.gqlShareListService.updateShareList(this.shareList).subscribe();
        this.editingName = false;
    }

    addProductToShareList(selectedItem: any) {
        const product: IProduct = selectedItem.item.product;
        this.resetFbModal();
        this.adjQuantity(product.usanaId, 1);
        this.analyticsService.addToShareListEvent('shareList', product.usanaId);
    }

    editName() {
        this.unchangedName = this.shareList.listName;
        this.editingName = true;
    }

    cancelEditingName() {
        if (this.editingName) {
            this.editingName = false;
            this.shareList.listName = this.unchangedName;
        }
    }

    setPageTitle() {
        this.markupService.setTitleFromTranslationTag("cart.manage.share.lists");
    }

    showChangeShareListNameInfo(): void {
        this.confirmDialogService.open("lg", {title: "cart.change.sharelist.name.tooltip.title"}, [{
            body: "cart.change.sharelist.name.tooltip",
            translate: true
        }], {}, {show: false});
    }

    resetFbModal() {
        if (this.facebookButtonComponent) {
            this.facebookButtonComponent.resetModalTimer();
        }
    }
}
